import ListingTileSkeleton from './ListingTileSkeleton';

/**
 * The card strip of a showcase row, without its heading.
 *
 * Split out from ShowcaseRowSkeleton because the two product-detail rows
 * ("More like this" and "Other from {seller}") already know their own titles
 * and render the real heading immediately — only the cards are waiting. Showing
 * the real heading over placeholder cards is both more honest and closer to the
 * finished row than a skeleton bar where a known title belongs.
 *
 * `.product-list-item` fixes each card at the same 300px (185 on mobile) the
 * loaded slider uses, and the tile inside is the same component the browse grid
 * renders, so all three stay in step by construction.
 */

interface ShowcaseStripSkeletonProps {
  /** Cards to draw. Six covers the widest viewport that shows a full strip. */
  cards?: number;
  /**
   * Set when the row it stands in for renders `<ListingSlider noMargin>`.
   *
   * A slider that is NOT `noMargin` carries `slidesOffsetBefore={25}`, and the
   * placeholder had no equivalent — so its first card sat flush against the
   * left edge and the real cards arrived 25px to the right of it. The
   * placeholder has to be indented exactly where the strip will be, or it is
   * not standing in for it.
   */
  flush?: boolean;
}

export function ShowcaseStripSkeleton({ cards = 6, flush = false }: ShowcaseStripSkeletonProps) {
  return (
    <div className="relative" data-testid="showcase-strip-skeleton" aria-hidden="true">
      {/* The loaded strip is a Swiper; a plain flex row of the same slide class
          produces the same boxes without the library. */}
      <div className={`listing-slider flex overflow-hidden${flush ? '' : ' showcase-strip-skeleton--inset'}`}>
        {Array.from({ length: cards }, (_, i) => (
          <div className="product-list-item shrink-0" key={`strip-skeleton-${i}`}>
            <ListingTileSkeleton />
          </div>
        ))}
      </div>
    </div>
  );
}

export default ShowcaseStripSkeleton;
